<script setup lang="ts">
  import { computed, onMounted } from "vue";
  import { Loader2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Progress } from "@/components/ui/progress";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { storeToRefs } from "pinia";
  import { useSurveyProfilesStore } from "@stores/survey/surveyProfiles";

  interface Issue {
    severity: "error" | "warning" | "info";
    card_token?: string;
    card_name?: string;
    detail: string;
  }

  interface ProgrammingCheck {
    status: "none" | "running" | "done";
    launched_at?: string;
    progress?: { done: number; total: number };
    report?: {
      respondents: number;
      completes: number;
      loi_minutes: { min: number; avg: number; max: number } | null;
      questions: { min: number; avg: number; max: number } | null;
      issues: Issue[];
    };
  }

  const SEVERITIES = {
    error: {
      title: "To fix",
      card: "border-status-error-border bg-status-error-soft",
      label: "text-status-error-strong",
    },
    warning: {
      title: "To check",
      card: "border-status-warning-border bg-status-warning-soft",
      label: "text-status-warning-strong",
    },
    info: {
      title: "Good to know",
      card: "border-status-info-border bg-status-info-soft",
      label: "text-status-info-strong",
    },
  };

  const props = defineProps<{ surveyToken: string; disabled?: boolean }>();
  const emit = defineEmits<{ rerun: []; fix: [prompt: string]; card: [token: string] }>();

  const surveyProfilesStore = useSurveyProfilesStore();
  const { programmingCheck, checking } = storeToRefs(surveyProfilesStore);
  const check = computed(() => {
    const loaded = programmingCheck.value as (ProgrammingCheck & { surveyToken: string }) | null;
    return loaded?.surveyToken === props.surveyToken ? loaded : null;
  });

  const report = computed(() => check.value?.report ?? null);
  const counts = computed(() => check.value?.progress ?? { done: 0, total: 0 });
  const progress = computed(() =>
    counts.value.total ? Math.round((counts.value.done / counts.value.total) * 100) : 0,
  );
  const stats = computed(() => {
    if (!report.value) return [];
    const { respondents, completes, loi_minutes, questions } = report.value;
    return [
      { label: "respondents", value: respondents },
      { label: "completed", value: `${respondents ? Math.round((completes / respondents) * 100) : 0} %` },
      { label: "questions", value: questions ? `≈ ${questions.avg}` : "—" },
      { label: "length", value: loi_minutes ? `≈ ${loi_minutes.avg} min` : "—" },
    ];
  });
  const issues = computed(() => report.value?.issues ?? []);
  const ranAt = computed(() =>
    check.value?.launched_at
      ? new Date(check.value.launched_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })
      : "",
  );

  function fix(issue: Issue) {
    const where = issue.card_name ? ` on “${issue.card_name}”` : "";
    emit("fix", `Help me fix this issue found by the programming check${where}: ${issue.detail}`);
  }

  onMounted(() => surveyProfilesStore.refresh(props.surveyToken));
</script>

<template>
  <EmptyState
    v-if="check?.status === 'none'"
    size="sm"
    title="No programming check yet"
    description="Check the programming to see what will not work before going live." />
  <div
    v-else-if="check"
    class="flex flex-col text-xs">
    <div class="flex h-10 shrink-0 items-center gap-2 border-y border-secondary bg-muted px-5">
      <span class="heading-label">Programming check</span>
      <span
        v-if="ranAt"
        class="text-muted-foreground">
        · {{ ranAt }}
      </span>
      <Button
        v-if="check.status !== 'running'"
        variant="outline"
        size="sm"
        class="ml-auto h-6 px-2.5 text-xs"
        :disabled="disabled || checking"
        @click="emit('rerun')">
        <Loader2
          v-if="checking"
          class="size-3 animate-spin" />
        Check again
      </Button>
    </div>

    <div
      v-if="check.status === 'running'"
      class="flex flex-col gap-1.5 px-5 py-4">
      <span class="text-muted-foreground">
        <template v-if="counts.total">
          Fake respondents are walking the questionnaire… {{ counts.done }}/{{ counts.total }}
        </template>
        <template v-else>Preparing the profiles to test…</template>
      </span>
      <Progress :model-value="progress" />
    </div>

    <template v-else-if="report">
      <div class="grid grid-cols-4 divide-x border-b px-5 py-4">
        <div
          v-for="stat in stats"
          :key="stat.label"
          class="flex flex-col gap-0.5 px-3 first:pl-0">
          <span class="heading-page whitespace-nowrap">{{ stat.value }}</span>
          <span class="text-muted-foreground">{{ stat.label }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-2.5 px-5 py-4">
        <p
          v-if="!issues.length"
          class="text-status-success-strong">
          No issue found on the paths tested.
        </p>
        <article
          v-for="(issue, index) in issues"
          :key="index"
          class="flex flex-col gap-1.5 rounded-lg border px-3.5 py-3"
          :class="SEVERITIES[issue.severity].card">
          <div class="flex items-center gap-1.5">
            <span
              class="heading-label"
              :class="SEVERITIES[issue.severity].label">
              {{ SEVERITIES[issue.severity].title }}
            </span>
            <template v-if="issue.card_token">
              <span :class="SEVERITIES[issue.severity].label">·</span>
              <button
                class="font-semibold text-foreground underline underline-offset-2"
                @click="emit('card', issue.card_token)">
                {{ issue.card_name }}
              </button>
            </template>
          </div>
          <span class="leading-relaxed text-foreground-secondary">{{ issue.detail }}</span>
          <Button
            v-if="issue.severity !== 'info'"
            variant="outline"
            size="sm"
            class="h-[26px] self-end bg-background px-2.5 text-xs"
            :disabled="disabled"
            @click="fix(issue)">
            <AiStar class="size-3" />
            Fix with Episto AI
          </Button>
        </article>
      </div>
    </template>
  </div>
</template>
